import GraphQLClient from 'ts/GraphQLClient'
import { RepoParams } from 'ts/types'
import {
  CloudEngineeringTask,
  CreateMAPContractInput,
  MAPContract,
  TagResourcesResult,
  UpdateMAPContractInput
} from '../types'
import { CREATE_MAP_CONTRACT } from './graphql/createMapContract'
import { GET_CLOUD_ENGINEERING_TASKS } from './graphql/getCloudEngineeringTasks'
import { GET_MAP_CONTRACTS } from './graphql/getMapContracts'
import { TAG_RESOURCES } from './graphql/tagResources'
import { UPDATE_MAP_CONTRACT } from './graphql/updateMapContract'

export default class MapManagementRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, GRAPHQL_API_KEY }: RepoParams) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, apiKey: GRAPHQL_API_KEY })
  }

  async getMapContracts(token: string, status?: string): Promise<MAPContract[]> {
    const data = await this.client.request<{
      getMapContracts: { data: MAPContract[]; error?: { message: string } }
    }>({
      query: GET_MAP_CONTRACTS,
      variables: { token, status }
    })

    if (data.getMapContracts.error) {
      throw new Error(data.getMapContracts.error.message)
    }

    return data.getMapContracts.data || []
  }

  async createMapContract(token: string, input: CreateMAPContractInput): Promise<MAPContract> {
    const data = await this.client.request<{
      createMapContract: { data: MAPContract; error?: { message: string } }
    }>({
      query: CREATE_MAP_CONTRACT,
      variables: { token, input }
    })

    if (data.createMapContract.error) {
      throw new Error(data.createMapContract.error.message)
    }

    return data.createMapContract.data
  }

  async updateMapContract(token: string, input: UpdateMAPContractInput): Promise<MAPContract> {
    const data = await this.client.request<{
      updateMapContract: { data: MAPContract; error?: { message: string } }
    }>({
      query: UPDATE_MAP_CONTRACT,
      variables: { token, input }
    })

    if (data.updateMapContract.error) {
      throw new Error(data.updateMapContract.error.message)
    }

    return data.updateMapContract.data
  }

  async tagResources(token: string, mapContractId: string): Promise<TagResourcesResult> {
    const data = await this.client.request<{
      tagResources: { data: TagResourcesResult; error?: { message: string } }
    }>({
      query: TAG_RESOURCES,
      variables: { token, mapContractId }
    })

    if (data.tagResources.error) {
      throw new Error(data.tagResources.error.message)
    }

    return data.tagResources.data
  }

  async getCloudEngineeringTasks(
    token: string,
    filters?: { mapContractId?: string; status?: string; assignedTo?: string; type?: string }
  ): Promise<CloudEngineeringTask[]> {
    const data = await this.client.request<{
      getCloudEngineeringTasks: { data: CloudEngineeringTask[]; error?: { message: string } }
    }>({
      query: GET_CLOUD_ENGINEERING_TASKS,
      variables: { token, ...filters }
    })

    if (data.getCloudEngineeringTasks.error) {
      throw new Error(data.getCloudEngineeringTasks.error.message)
    }

    return data.getCloudEngineeringTasks.data || []
  }
}
